/**
 * DeletionGraceBanner — D-12 cancel path #1 (in-app banner).
 *
 * Renders when user.deletion_scheduled_at is non-null.
 * Every user-facing string is wrapped in t('gdpr.deletion.*') (D-22 i18n parity).
 * Reskinned to brand tokens (Plan 02-04): destructive semantic token for the
 * deletion-warning hue (D-32 forbids amber/orange status colors).
 */
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';

export function DeletionGraceBanner() {
  const { t } = useTranslation();
  const { user, refreshSession } = useAuth();

  if (!user?.deletion_scheduled_at) {
    return null;
  }

  const date = new Date(user.deletion_scheduled_at).toLocaleDateString(
    user.locale === 'de' ? 'de-DE' : 'en-GB',
    { year: 'numeric', month: 'long', day: 'numeric' },
  );

  const handleCancel = async () => {
    try {
      await api.post('/api/account/delete/cancel');
      await refreshSession();
    } catch {
      // GDPR-critical: do NOT silently no-op on cancel failure. Surface a
      // sonner error so the user can retry instead of believing the
      // deletion was cancelled when it wasn't (WR-07).
      toast.error(t('app.deletion.cancel_failed'));
    }
  };

  return (
    <div
      role="alert"
      data-testid="deletion-grace-banner"
      className="flex items-center gap-4 px-6 py-3 text-sm bg-destructive/10 text-destructive border-b border-destructive/30"
    >
      <span className="flex-1">
        {t('gdpr.deletion.banner_message', { date })}
      </span>
      <Button
        type="button"
        variant="default"
        size="sm"
        onClick={handleCancel}
        className="whitespace-nowrap"
      >
        {t('gdpr.deletion.cancel_button')}
      </Button>
    </div>
  );
}
